import './MixedBillingSegmentModal.scss';
import { useState } from 'react';
import cn from 'classnames';
import * as Dfns from 'date-fns/fp';
import { Modal } from 'react-bootstrap';
import { RawBillingSegmentMapEntry } from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { GraphTableToggleButton } from '@Components/GraphTableToggleButton';
import { formatDateRange, isNewBillingSegmentValue } from './segmentationMappingUtils';
import { Brand50, Neutral200, Neutral300, Neutral500 } from '../../utils/colors';

export interface MixedBillingSegmentData {
  segmentName: string;
  entries: Record<string, string | null>[];
  committedEntries?: RawBillingSegmentMapEntry[];
}

interface MixedBillingSegmentModalProps {
  data: MixedBillingSegmentData;
  show: boolean;
  setShow: (show: boolean) => void;
}

interface ParsedEntry {
  label: string;
  start_date: string;
  end_date: string | null;
}

const CELL_TO_BAR_RATIO = 0.4;
const Y_AXIS_WIDTH = 420;
const ROW_HEIGHT = 56;
const MIN_TICK_WIDTH_PX = 55;

const renderBarBackground = (props: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  index?: number;
}) => {
  const { x, y, width, height, index } = props;
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={(index ?? 0) % 2 === 0 ? Neutral200 : 'white'}
    />
  );
};

const renderBarShape = (props: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
}) => {
  const { x, y, width, height, fill } = props;
  const safeHeight = height ?? 0;
  const safeY = y ?? 0;
  const cellHeight = safeHeight * CELL_TO_BAR_RATIO;
  const offsetY = (safeHeight - cellHeight) / 2;
  return <rect x={x} y={safeY + offsetY} width={width} height={cellHeight} fill={fill} />;
};

export const MixedBillingSegmentModal = ({
  data,
  show,
  setShow
}: MixedBillingSegmentModalProps) => {
  const [barGraphView, setBarGraphView] = useState(false);
  const [chartWidth, setChartWidth] = useState<number>(0);
  const { segmentName, entries, committedEntries } = data;

  const parsedEntries: ParsedEntry[] = entries
    .filter(
      (entry): entry is Record<string, string | null> & { start_date: string } =>
        entry.start_date != null
    )
    .map(entry => ({
      label: entry[segmentName] ?? '',
      start_date: entry.start_date,
      end_date: entry.end_date ?? null
    }));

  const today = Dfns.format('yyyy-MM-dd', new Date());

  const latestKnownDate = Dfns.format(
    'yyyy-MM-dd',
    Dfns.add({ months: 1 })(
      Dfns.parseISO(
        parsedEntries.reduce((max, e) => {
          const end = e.end_date ?? today;
          return end > max ? end : max;
        }, today)
      )
    )
  );

  const chartData = parsedEntries
    .map(entry => ({
      label: entry.label,
      start_date: entry.start_date,
      end_date: entry.end_date,
      timeRange: [
        Dfns.parseISO(entry.start_date).getTime(),
        Dfns.parseISO(entry.end_date ?? latestKnownDate).getTime()
      ] as [number, number]
    }))
    .sort((a, b) => a.timeRange[0] - b.timeRange[0]);

  const yAxisTick = (props: {
    x?: number | string;
    y?: number | string;
    index?: number;
    payload?: { value?: string };
  }) => {
    const xNum = Number(props.x) || 0;
    const yNum = Number(props.y) || 0;
    const idx = props.index ?? 0;
    const label = props.payload?.value ?? '';
    const entry = chartData[idx];
    const dateStr = entry ? formatDateRange(entry.start_date, entry.end_date) : '';
    const isEdit =
      committedEntries && entry
        ? isNewBillingSegmentValue(
            { label: entry.label, startDate: entry.start_date, endDate: entry.end_date },
            committedEntries,
            segmentName
          )
        : false;
    return (
      <foreignObject x={0} y={yNum - ROW_HEIGHT / 2} width={xNum} height={ROW_HEIGHT}>
        <div className='y-axis-tick'>
          <div className='y-axis-tick-label-group'>
            <span className={cn('y-axis-tick-label', { 'edited-entry': isEdit })}>{label}</span>
            {isEdit && <span className='not-saved-label'>Not saved</span>}
          </div>
          <div className={cn('y-axis-tick-dates', { 'edited-entry': isEdit })}>{dateStr}</div>
        </div>
      </foreignObject>
    );
  };

  const timeMin = Math.min(...chartData.map(d => d.timeRange[0]));
  const timeMax = Math.max(...chartData.map(d => d.timeRange[1]));

  const allMonthTicks = Dfns.eachMonthOfInterval({
    start: Dfns.startOfMonth(Dfns.fromUnixTime(timeMin / 1000)),
    end: Dfns.add({ months: 1 })(Dfns.startOfMonth(Dfns.fromUnixTime(timeMax / 1000)))
  }).map(date => date.getTime());

  const plotWidth = Math.max(0, chartWidth - Y_AXIS_WIDTH - 40);
  const maxTicks = plotWidth > 0 ? Math.floor(plotWidth / MIN_TICK_WIDTH_PX) : 1;
  const tickStep = Math.max(1, Math.ceil(allMonthTicks.length / maxTicks));
  const xAxisTicks = allMonthTicks.filter((_, i) => i % tickStep === 0);

  // Grouped and ordered for table view
  const groupedEntries: Record<string, { start_date: string; end_date: string | null }[]> = {};
  parsedEntries.forEach(({ label, start_date, end_date }) => {
    if (!groupedEntries[label]) {
      groupedEntries[label] = [];
    }
    groupedEntries[label].push({ start_date, end_date });
  });
  Object.values(groupedEntries).forEach(ranges =>
    ranges.sort((a, b) => a.start_date.localeCompare(b.start_date))
  );

  return (
    <Modal
      className='mixed-billing-segment-modal'
      show={show}
      onHide={() => setShow(false)}
      dialogClassName={
        barGraphView ? 'mixed-billing-segment-modal-lg' : 'mixed-billing-segment-modal-sm'
      }
    >
      <Modal.Header className='mixed-billing-segment-modal-header' closeButton>
        <span className='main-header'>Mixed Dates</span>
        <GraphTableToggleButton
          graphView={barGraphView}
          setGraphView={setBarGraphView}
          widgetName='mixedBillingSegmentModal'
        />
      </Modal.Header>
      <Modal.Body className='mixed-billing-segment-modal-body'>
        {barGraphView ? (
          <ResponsiveContainer
            width='100%'
            height={parsedEntries.length * ROW_HEIGHT + 40}
            onResize={width => setChartWidth(width)}
          >
            <BarChart
              layout='vertical'
              data={chartData}
              margin={{ left: 0, right: 40, top: 8, bottom: 8 }}
              barCategoryGap={0}
            >
              <XAxis
                dataKey='timeRange'
                scale='time'
                type='number'
                domain={[timeMin, timeMax]}
                ticks={xAxisTicks}
                tickFormatter={ts => Dfns.format("MMM ''yy", Dfns.fromUnixTime(ts / 1000))}
                orientation='top'
                tickLine={false}
                axisLine={false}
                interval={0}
              />
              <YAxis
                type='category'
                dataKey='label'
                width={Y_AXIS_WIDTH}
                tickLine={false}
                axisLine={false}
                tick={yAxisTick}
                interval={0}
              />
              <CartesianGrid strokeWidth={1} stroke={Neutral300} horizontal={false} />
              <Bar
                dataKey='timeRange'
                fill={Brand50}
                background={renderBarBackground}
                shape={renderBarShape}
              >
                {chartData.map((entry, index) => {
                  const isEdit = committedEntries
                    ? isNewBillingSegmentValue(
                        {
                          label: entry.label,
                          startDate: entry.start_date,
                          endDate: entry.end_date
                        },
                        committedEntries,
                        segmentName
                      )
                    : false;
                  return <Cell key={index} fill={isEdit ? Neutral500 : Brand50} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className='mixed-segment-table'>
            {Object.entries(groupedEntries).map(([label, ranges]) => (
              <div key={label} className='mixed-segment-values'>
                <div className='mixed-segment-values-label'>{label}</div>
                {ranges.map((range, i) => {
                  const isEdit =
                    committedEntries != null &&
                    isNewBillingSegmentValue(
                      { label, startDate: range.start_date, endDate: range.end_date },
                      committedEntries,
                      segmentName
                    );
                  return (
                    <div
                      key={i}
                      className={cn('mixed-segment-date-range', { 'edited-entry': isEdit })}
                    >
                      {formatDateRange(range.start_date, range.end_date)}
                      {isEdit && <span className='not-saved-label'>Not saved</span>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
